import React, { useState, useEffect } from 'react';
import { createUserWithEmailAndPassword } from 'firebase/auth';
import { doc, setDoc, collection, getDoc } from 'firebase/firestore';
import { portalAuth, portalDb, sweepDb } from '../utils/firebase';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { toast, Toaster } from 'react-hot-toast';

// Helper function to normalize text for searching by removing special characters
const normalizeForSearch = (text) => {
  if (!text) return '';
  return text.toLowerCase()
    .replace(/[^a-z0-9]/g, '') // Remove all non-alphanumeric characters
    .trim();
};

const RegisterPage = () => {
  const [searchParams] = useSearchParams();
  const [reportId, setReportId] = useState(null);
  const [reportInfo, setReportInfo] = useState(null);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [userType, setUserType] = useState('client'); // Default to client for users coming from shared reports
  const [companyName, setCompanyName] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  
  const navigate = useNavigate();

  // Check for reportId in URL and fetch basic info about the report
  useEffect(() => {
    const fetchReportInfo = async () => {
      const reportIdFromUrl = searchParams.get('reportId');
      if (reportIdFromUrl) {
        setReportId(reportIdFromUrl);
        
        try {
          // Get basic info about the report to display
          const reportDoc = await getDoc(doc(sweepDb, 'inspections', reportIdFromUrl));
          if (reportDoc.exists()) {
            const data = reportDoc.data();
            setReportInfo({
              clientName: data.clientName,
              propertyAddress: data.propertyAddress,
              dateOfInspection: data.dateOfInspection
            });
            
            // Don't auto-fill name fields since the person registering might be 
            // different from the client in the report (like a realtor or attorney)
            // - The person will manually fill in their own information
          }
        } catch (error) {
          console.error('Error fetching report info:', error);
        }
      }
    };
    
    fetchReportInfo();
  }, [searchParams]);

  const handleRegister = async (e) => {
    e.preventDefault();
    
    if (!firstName || !lastName || !email || !password || !userType) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    try {
      setIsLoading(true);
      
      // Create user in Firebase Auth
      const userCredential = await createUserWithEmailAndPassword(
        portalAuth, 
        email, 
        password
      );
      
      const user = userCredential.user;
      
      // Initialize savedReports array
      const savedReports = [];
      
      // If a reportId was provided, add it to the user's savedReports
      if (reportId) {
        savedReports.push(reportId);
      }
      
      // Format company name if provided (preserve original case which is important for display)
      const formattedCompanyName = companyName ? companyName.trim() : '';
      
      // Create normalized version for improved searching
      const normalizedCompanyName = normalizeForSearch(formattedCompanyName);
      
      // Store additional user data in Firestore
      await setDoc(doc(portalDb, 'users', user.uid), {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        email: email.trim().toLowerCase(), // Ensure email is lowercase for consistency
        userType,
        companyName: formattedCompanyName,
        companyNameLower: formattedCompanyName.toLowerCase(), // Add lowercase version for better searching
        companyNameNormalized: normalizedCompanyName, // Add normalized version (removes special chars)
        createdAt: new Date().toISOString(),
        savedReports: savedReports, // Include the report if provided
        previousReports: [], // Initialize empty array for previous reports
        lastLogin: new Date().toISOString()
      });
      
      // Show appropriate success message based on whether a report was imported
      if (reportId) {
        toast.success('Account created successfully! The report has been added to your account.');
      } else {
        toast.success('Account created successfully!');
      }
      
      // Redirect to dashboard
      setTimeout(() => {
        navigate('/');
      }, 1500);
      
    } catch (error) {
      console.error(error);
      if (error.code === 'auth/email-already-in-use') {
        toast.error('Email is already in use');
      } else {
        toast.error('Error creating account: ' + error.message);
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 py-8 overflow-auto">
      <Toaster 
        position="top-center" 
        containerStyle={{
          zIndex: 9999999, // Very high z-index to ensure toasts appear above all other elements
          position: "relative",
          pointerEvents: "auto"
        }}
        toastOptions={{
          // Custom styles for the individual toasts
          style: {
            zIndex: 9999999,
            position: "relative"
          }
        }}
      />
      
      <div className="max-w-md w-full mx-4 space-y-6 p-6 md:p-8 bg-white rounded-3xl shadow-xl border border-slate-100 my-8">
        <div className="flex flex-col items-center">
          <div className="w-20 h-20 md:w-24 md:h-24 bg-gradient-to-r from-blue-500 to-cyan-400 rounded-full flex items-center justify-center mb-4 shadow-lg">
            <img
              src="/oil.png"
              alt="OilTank Portal Logo"
              className="w-14 h-14 md:w-16 md:h-16 brightness-0 invert"
            />
          </div>
          <h2 className="text-2xl md:text-3xl font-bold bg-gradient-to-r from-blue-500 to-cyan-400 bg-clip-text text-transparent">Create Account</h2>
        </div>
        
        <form className="mt-6 space-y-5" onSubmit={handleRegister}>
          {/* Show report info if available */}
          {reportInfo && (
            <div className="mb-4 p-4 bg-blue-50 border-l-4 border-blue-500 rounded-md">
              <h3 className="font-medium text-blue-700">This report will be added to your account:</h3>
              <div className="mt-2 text-sm text-gray-600">
                <p><span className="font-medium">Property:</span> {reportInfo.propertyAddress}</p>
                <p><span className="font-medium">Client:</span> {reportInfo.clientName}</p>
                <p><span className="font-medium">Date:</span> {
                  new Date(reportInfo.dateOfInspection).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })
                }</p>
              </div>
            </div>
          )}
          
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-1">
                  First Name *
                </label>
                <input
                  id="firstName"
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  className="appearance-none relative block w-full px-4 py-3 border border-gray-300 placeholder-gray-400 text-gray-900 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter first name"
                  required
                />
              </div>
              
              <div>
                <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-1">
                  Last Name *
                </label>
                <input
                  id="lastName"
                  type="text"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  className="appearance-none relative block w-full px-4 py-3 border border-gray-300 placeholder-gray-400 text-gray-900 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter last name"
                  required
                />
              </div>
            </div>
            
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                Email Address *
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="appearance-none relative block w-full px-4 py-3 border border-gray-300 placeholder-gray-400 text-gray-900 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Enter your email"
                required
              />
            </div>
            
            <div>
              <label htmlFor="userType" className="block text-sm font-medium text-gray-700 mb-1">
                I am a: *
              </label>
              <select
                id="userType"
                value={userType}
                onChange={(e) => setUserType(e.target.value)}
                className="appearance-none relative block w-full px-4 py-3 border border-gray-300 placeholder-gray-400 text-gray-900 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              >
                <option value="">Select your role</option>
                <option value="realtor">Realtor</option>
                <option value="attorney">Attorney</option>
                <option value="client">Homeowner/Client</option>
                <option value="other">Other</option>
              </select>
            </div>
            
            <div>
              <label htmlFor="companyName" className="block text-sm font-medium text-gray-700 mb-1">
                Company Name (Optional)
              </label>
              <input
                id="companyName"
                type="text"
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                className="appearance-none relative block w-full px-4 py-3 border border-gray-300 placeholder-gray-400 text-gray-900 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Enter company name"
              />
            </div>
            
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                Password *
              </label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="appearance-none relative block w-full px-4 py-3 border border-gray-300 placeholder-gray-400 text-gray-900 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Create a password"
                required
                minLength={6}
              />
              <p className="text-xs text-gray-500 mt-1">Must be at least 6 characters</p>
            </div>
            
            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1">
                Confirm Password *
              </label>
              <input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="appearance-none relative block w-full px-4 py-3 border border-gray-300 placeholder-gray-400 text-gray-900 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Confirm your password"
                required
              />
            </div>
          </div>
          
          <button
            type="submit"
            disabled={isLoading}
            className="w-full flex items-center justify-center px-4 py-3 border border-transparent text-base font-medium rounded-lg text-white bg-[#2B7FE8] hover:bg-[#2472d4] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
          >
            {isLoading ? 'Creating Account...' : 'Create Account'}
          </button>
        </form>
        
        <div className="text-center text-sm text-gray-600">
          <p>
            Already have an account?{' '}
            <Link to="/login" className="text-blue-500 hover:text-blue-600 hover:underline">
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage; 